import React from "react"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import LayersOutlined from "@mui/icons-material/LayersOutlined"
import { useTranslation } from "react-i18next"
import { styles } from "./ConfirmationModal.styles"

const ConfirmationModalUI = ({
  open = false,
  onClose,
  icon = <LayersOutlined fontSize="medium" />,
  iconColor = "blueBright",
  iconBgColor,
  showIcon = true,
  showCloseButton = false,
  title,
  description,
  children,
  primaryButton,
  secondaryButton,
  actions,
  maxWidth = 540,
  disableBackdropClick = true,
}) => {
  const { t } = useTranslation()

  const handleClose = (event, reason) => {
    if (
      disableBackdropClick &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return
    }
    onClose?.(event, reason)
  }

  const defaultSecondary = {
    label: t("cancel"),
    onClick: onClose,
    variant: "outlined",
    color: "blueBright",
  }

  const defaultPrimary = {
    label: t("accept"),
    onClick: onClose,
    variant: "contained",
    color: "blueBright",
  }

  const buttonsToRender =
    actions ||
    [
      secondaryButton !== false && secondaryButton !== null
        ? {
            ...defaultSecondary,
            ...(typeof secondaryButton === "object" ? secondaryButton : {}),
            ...(typeof secondaryButton === "string"
              ? { label: secondaryButton }
              : {}),
          }
        : null,
      primaryButton !== false && primaryButton !== null
        ? {
            ...defaultPrimary,
            ...(typeof primaryButton === "object" ? primaryButton : {}),
            ...(typeof primaryButton === "string"
              ? { label: primaryButton }
              : {}),
          }
        : null,
    ].filter(Boolean)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={disableBackdropClick}
      PaperProps={{ sx: styles.paper(maxWidth) }}
    >
      {showCloseButton && (
        <IconButton
          sx={styles.closeButton}
          aria-label={t("close", "Close")}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent sx={styles.content}>
        {showIcon && icon && (
          <Box sx={styles.iconWrapper(iconColor, iconBgColor)}>{icon}</Box>
        )}
        {title && (
          <Typography variant="title" sx={styles.title}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" sx={styles.description}>
            {description}
          </Typography>
        )}
        {children}
        {buttonsToRender.length > 0 && (
          <Box sx={styles.actions}>
            {buttonsToRender.map((btn, index) => {
              const {
                label,
                onClick,
                variant = "contained",
                color = "blueBright",
                disabled = false,
                disableElevation = true,
                sx,
                ...rest
              } = btn

              return (
                <Button
                  key={index}
                  variant={variant}
                  color={color}
                  onClick={onClick}
                  disabled={disabled}
                  disableElevation={disableElevation}
                  sx={[styles.actionBtn, ...(Array.isArray(sx) ? sx : [sx])]}
                  {...rest}
                >
                  {label}
                </Button>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export { ConfirmationModalUI }
export default ConfirmationModalUI
